import { Combobox } from "@ttc3k/trekker";
import { Text } from "@ttc3k/trekker";
import { useTranslation } from "react-i18next";
import { Flex } from "styled-system/jsx";
import { useEventKeywords } from "./useEventKeywords";
import { EmptyState } from "shared/components";
import { useEventCreateEditContext } from "../../EventCreateEditContext/useEventCreateEditContext";
import { Collection } from "shared/types";

export const EventKeywords = () => {
  const { t } = useTranslation("events", {
    keyPrefix: "APP.MODAL.EVENT_DETAILS.EVENT_KEYWORDS",
  });

  const {
    globalEventTags,
    organizationEventTags,
    isOrganizationUser,
    defaultOrganizationId,
  } = useEventCreateEditContext();

  const {
    filteredGlobalTagsCollection,
    filteredOrganizationTags,
    filteredOrganizationTagsCollection,
    selectedGlobalTags,
    selectedOrganizationTags,
    handleFilterGlobalTags,
    handleFilterOrganizationTags,
    handleOrgTagsComboboxOpenChange,
    handleGlobalTagsComboboxOpenChange,
    setValue,
  } = useEventKeywords({
    globalEventTags,
    organizationEventTags,
  });

  return (
    <>
      <KeywordGroup
        title={t("GLOBAL.TITLE")}
        description={t("GLOBAL.DESCRIPTION")}
        placeholder={t("PLACEHOLDER_LABEL")}
        collection={filteredGlobalTagsCollection}
        selectedTags={selectedGlobalTags}
        onTagValueChange={value =>
          setValue("tags.global", value, { shouldDirty: true })
        }
        onTagInputValueChange={search => handleFilterGlobalTags(search)}
        onComboboxOpenChange={open => handleGlobalTagsComboboxOpenChange(open)}
        hasGroups={true}
      />
      {isOrganizationUser && filteredOrganizationTags.length > 0 ? (
        <KeywordGroup
          title={t("ORGANIZATION.TITLE")}
          description={t("ORGANIZATION.DESCRIPTION")}
          placeholder={t("PLACEHOLDER_LABEL")}
          collection={filteredOrganizationTagsCollection}
          selectedTags={selectedOrganizationTags.filter(tag =>
            filteredOrganizationTags.find(orgTag => orgTag.value === tag),
          )}
          onTagValueChange={value =>
            setValue(
              "tags.organizationSpecific",
              [
                // Tags from the current organization
                ...value,
                // Tags from other organizations
                ...selectedOrganizationTags.filter(
                  tag =>
                    !filteredOrganizationTags.find(
                      orgTag => orgTag.value === tag,
                    ),
                ),
              ],
              { shouldDirty: true },
            )
          }
          onTagInputValueChange={search => handleFilterOrganizationTags(search)}
          onComboboxOpenChange={open => handleOrgTagsComboboxOpenChange(open)}
        />
      ) : isOrganizationUser && defaultOrganizationId ? (
        <EmptyState
          text={t("EMPTY")}
          ctaButtonProps={{
            children: t("EMPTY_CTA"),
            size: "md",
            onClick: () => {
              // Open Custom Tags in Organization Settings via URL deep link.
              location.href = `/app/organization/${defaultOrganizationId}/home?view=SETTINGS&settingsSubView=CUSTOM_TAGS`;
            },
          }}
        />
      ) : null}
    </>
  );
};

type KeywordGroupProps = {
  title: string;
  description: string;
  placeholder: string;
  collection: Collection;
  selectedTags: string[];
  hasGroups?: boolean;
  onTagValueChange: (value: string[]) => void;
  onTagInputValueChange: (inputValue: string) => void;
  onComboboxOpenChange: (open: boolean) => void;
};

const KeywordGroup = ({
  title,
  description,
  placeholder,
  selectedTags,
  onTagValueChange,
  onTagInputValueChange,
  hasGroups,
  collection,
  onComboboxOpenChange,
}: KeywordGroupProps) => {
  const { t } = useTranslation("events", {
    keyPrefix: "APP.LOOKUPS",
  });

  return (
    <Flex flexDir="column" gap="150">
      <Text visual="bodySemiBold">{title}</Text>
      <Text visual="bodyRegular" color="text.mid">
        {description}
      </Text>
      <Combobox.Root
        value={selectedTags}
        onValueChange={d => onTagValueChange(d.value)}
        onInputValueChange={d => onTagInputValueChange(d.inputValue)}
        onOpenChange={d => onComboboxOpenChange(d.open)}
        positioning={{
          placement: "top",
        }}
        collection={collection}
        placeholder={selectedTags?.length === 0 ? `${placeholder}...` : ""}
        css={{
          "& [data-scope='combobox'][data-part='input'], [data-scope='combobox'][data-part='input-wrapper']":
            {
              bg: "white",
            },
        }}
        multiple
      >
        {hasGroups
          ? collection.group().map(([group, tags]) => (
              <Combobox.ItemGroup key={group}>
                <Combobox.ItemGroupLabel>{t(group)}</Combobox.ItemGroupLabel>
                {tags.map(item => (
                  <Combobox.Item key={item.value} item={item} {...item} />
                ))}
              </Combobox.ItemGroup>
            ))
          : collection.items.map(item => (
              <Combobox.Item key={item.value} item={item} {...item} />
            ))}
      </Combobox.Root>
    </Flex>
  );
};
